// KPI set editörü / katalog / fork modalı saf mantığı (M16 T10 adım 3, kural 10/11/15).
// Bileşenler ince kalır; ağaç kurma, taslak farkı, formül taslak doğrulaması, üçlü durum
// (tri-state) seçim ve kardeşler arası taşıma burada kilitlenir — vitest node ortamı, DOM yok.

import {
  isUnitTemplate,
  parseFormula,
  reachableUnits,
  resolveUnitTemplate,
  unitConversionFactor,
  unitDisplayName,
} from "@mahir/shared";
import { moveBy, planReorder, type DropPosition } from "./kpi-reorder";

/** Ağaç düğümü için gereken asgari alanlar (set göstergesi ve katalog metriği ikisi de uyar). */
export interface TreeRowLike {
  id: string;
  parentId: string | null;
  orderIndex: number;
  code: string;
  label: string;
}

export interface TreeRow<T extends TreeRowLike> {
  node: T;
  depth: number;
  hasChildren: boolean;
  /** doğrudan çocuk sayısı */
  childCount: number;
  /** alt ağaçtaki yaprak sayısı (kendisi yapraksa 1) */
  leafCount: number;
  /** kök ata id'si (kendisi kökse kendi id'si) */
  rootId: string;
}

export interface IndicatorTree<T extends TreeRowLike> {
  rows: TreeRow<T>[];
  byId: Map<string, TreeRow<T>>;
  childrenOf: Map<string | null, T[]>;
}

function compareSiblings<T extends TreeRowLike>(a: T, b: T): number {
  return a.orderIndex - b.orderIndex || a.label.localeCompare(b.label, "tr");
}

/**
 * Ön sıralı (pre-order) düz liste: kardeşler `orderIndex` → etiket sırasıyla; `parentId` listede
 * olmayan düğüm kök sayılır (yetim koruması). Döngüsel parentId zinciri sessizce kırılır.
 */
export function buildIndicatorTree<T extends TreeRowLike>(items: T[]): IndicatorTree<T> {
  const ids = new Set(items.map((i) => i.id));
  const childrenOf = new Map<string | null, T[]>();
  for (const it of items) {
    const p = it.parentId && ids.has(it.parentId) && it.parentId !== it.id ? it.parentId : null;
    const arr = childrenOf.get(p) ?? [];
    arr.push(it);
    childrenOf.set(p, arr);
  }
  for (const arr of childrenOf.values()) arr.sort(compareSiblings);

  const rows: TreeRow<T>[] = [];
  const byId = new Map<string, TreeRow<T>>();
  const visit = (
    parent: string | null,
    depth: number,
    rootId: string | null,
    seen: Set<string>,
  ) => {
    for (const node of childrenOf.get(parent) ?? []) {
      if (seen.has(node.id)) continue;
      seen.add(node.id);
      const kids = childrenOf.get(node.id) ?? [];
      const row: TreeRow<T> = {
        node,
        depth,
        hasChildren: kids.length > 0,
        childCount: kids.length,
        leafCount: 0,
        rootId: rootId ?? node.id,
      };
      rows.push(row);
      byId.set(node.id, row);
      visit(node.id, depth + 1, rootId ?? node.id, seen);
    }
  };
  visit(null, 0, null, new Set());

  // yaprak sayıları (alt ağaç) — ters sırada toplanır
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const r = rows[i]!;
    if (!r.hasChildren) r.leafCount = 1;
    const parentId = r.node.parentId;
    const parent = parentId ? byId.get(parentId) : undefined;
    if (parent) parent.leafCount += r.leafCount;
  }
  return { rows, byId, childrenOf };
}

/** Alt ağacın yaprak id'leri (düğüm yapraksa kendisi). */
export function subtreeLeafIds<T extends TreeRowLike>(
  tree: IndicatorTree<T>,
  nodeId: string,
): string[] {
  const out: string[] = [];
  const walk = (id: string): void => {
    const kids = tree.childrenOf.get(id) ?? [];
    if (kids.length === 0) {
      out.push(id);
      return;
    }
    for (const k of kids) walk(k.id);
  };
  if (tree.byId.has(nodeId)) walk(nodeId);
  return out;
}

export type TriState = "all" | "some" | "none";

/** Kural 11: kategori onay kutusu üçlü durum — alt ağaç yapraklarının seçim oranı. */
export function triStateFromSelection<T extends TreeRowLike>(
  nodeId: string,
  tree: IndicatorTree<T>,
  selectedLeafIds: ReadonlySet<string>,
): TriState {
  const leaves = subtreeLeafIds(tree, nodeId);
  if (leaves.length === 0) return "none";
  let n = 0;
  for (const id of leaves) if (selectedLeafIds.has(id)) n += 1;
  if (n === 0) return "none";
  return n === leaves.length ? "all" : "some";
}

/** Alt ağacı toptan seç / bırak; `select` verilmezse "all" ise bırakır, değilse seçer. */
export function toggleSubtree<T extends TreeRowLike>(
  nodeId: string,
  tree: IndicatorTree<T>,
  selectedLeafIds: ReadonlySet<string>,
  select?: boolean,
): Set<string> {
  const next = new Set(selectedLeafIds);
  const leaves = subtreeLeafIds(tree, nodeId);
  const want = select ?? triStateFromSelection(nodeId, tree, selectedLeafIds) !== "all";
  for (const id of leaves) {
    if (want) next.add(id);
    else next.delete(id);
  }
  return next;
}

/** Seçili yaprak sayısı — "Oluştur (N gösterge)" sayacı. */
export function countSelectedLeaves<T extends TreeRowLike>(
  tree: IndicatorTree<T>,
  selectedLeafIds: ReadonlySet<string>,
): number {
  let n = 0;
  for (const r of tree.rows) if (!r.hasChildren && selectedLeafIds.has(r.node.id)) n += 1;
  return n;
}

// ─── Taslak farkı (isDirty bar → toplu PATCH) ──────────────────────────────────

/**
 * PATCH'e giden alanlar — KpiSetIndicatorPatch anahtarlarıyla birebir. `unit` bilinçli olarak YOK:
 * birim müşteri genelindedir ve ayrı eylemle (`applyKpiUnitChange`) yazılır (birim varyantı DR4).
 */
export const INDICATOR_PATCH_KEYS = [
  "labelOverride",
  "aggregationOverride",
  "aggregationWeightOverride",
  "formulas",
  "activeFormulaId",
  "clientAccess",
  "linkedQuestionCode",
  "showChange",
  "increaseIsGood",
  "anomalyThreshold",
  "footnote",
  "parentId",
  "orderIndex",
  // Kırılım (0069 D7/D9): toplamın açık hesap grubu — IndicatorModal radyosu taslağa yazar
  "calculationGroupMetricId",
] as const;
export type IndicatorPatchKey = (typeof INDICATOR_PATCH_KEYS)[number];

type Patchable = { id: string } & Partial<Record<IndicatorPatchKey, unknown>>;

function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === "object" && typeof b === "object" && a !== null && b !== null) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

/**
 * Orijinal ↔ taslak satırları karşılaştırır; yalnız değişen anahtarları içeren patch listesi
 * döner (orijinalde olmayan taslak satır atlanır, değişmeyen satır listede yer almaz).
 */
export function diffIndicatorPatches<T extends Patchable>(
  original: readonly T[],
  draft: readonly T[],
): { id: string; patch: Partial<Record<IndicatorPatchKey, unknown>> }[] {
  const byId = new Map(original.map((o) => [o.id, o]));
  const out: { id: string; patch: Partial<Record<IndicatorPatchKey, unknown>> }[] = [];
  for (const d of draft) {
    const o = byId.get(d.id);
    if (!o) continue;
    const patch: Partial<Record<IndicatorPatchKey, unknown>> = {};
    for (const k of INDICATOR_PATCH_KEYS) {
      if (!sameValue(o[k], d[k])) patch[k] = d[k];
    }
    if (Object.keys(patch).length > 0) out.push({ id: d.id, patch });
  }
  return out;
}

// ─── Formül taslak doğrulaması (kural 10) ─────────────────────────────────────

export interface FormulaDraftCheck {
  ok: boolean;
  error?: string;
  position?: number;
  /** sözdizimi geçerli ama kod kümesinde olmayan referanslar */
  unknownRefs: string[];
  refs: string[];
}

/** `refs ⊆ codes` + sözdizimi; boş ifade hatadır. */
export function validateFormulaDraft(
  expression: string,
  codes: ReadonlySet<string>,
): FormulaDraftCheck {
  const trimmed = expression.trim();
  if (trimmed === "")
    return { ok: false, error: "İfade boş", position: 0, unknownRefs: [], refs: [] };
  const p = parseFormula(expression);
  if (!p.ok) return { ok: false, error: p.error, position: p.position, unknownRefs: [], refs: [] };
  const unknown = p.refs.filter((r) => !codes.has(r));
  if (unknown.length > 0) {
    return {
      ok: false,
      error: `Veri setinde yok: ${unknown.join(", ")}`,
      unknownRefs: unknown,
      refs: p.refs,
    };
  }
  return { ok: true, unknownRefs: [], refs: p.refs };
}

/** Sözdizimi hatası için işaretçi satırı: ifade altında `^` (monospace). */
export function caretLine(position: number): string {
  return `${" ".repeat(Math.max(0, position))}^`;
}

/** Sunucu döngü hatası `details.cycle` → "a → b → a". */
export function formatCyclePath(cycle: unknown): string | null {
  if (!Array.isArray(cycle)) return null;
  const parts = cycle.filter((c): c is string => typeof c === "string");
  return parts.length > 0 ? parts.join(" → ") : null;
}

/** Aktif formülü olmayan ya da aktif formülü set kodlarına göre geçersiz olan göstergeler. */
export function invalidFormulaIds(
  indicators: readonly {
    id: string;
    isDerived: boolean;
    formulas: readonly { isActive: boolean; expression: string }[];
  }[],
  codes: ReadonlySet<string>,
): Set<string> {
  const out = new Set<string>();
  for (const ind of indicators) {
    if (!ind.isDerived) continue;
    const active = ind.formulas.find((f) => f.isActive);
    if (!active || !validateFormulaDraft(active.expression, codes).ok) out.add(ind.id);
  }
  return out;
}

/**
 * Taslaktaki aktif formüller arasında döngü (istemci tarafı, tur bulgusu P3-27): a → b → a.
 * Sunucu `KPI_FORMULA_CYCLE` ile aynı şeyi söyler ama Kaydet'e basmadan sebep görünsün diye burada
 * da bakılır. İlk bulunan döngünün yolu döner (kod listesi, baş = son); yoksa null. Yalnız
 * sözdizimi geçerli aktif formüller gezilir; bilinmeyen kodlar kenar sayılmaz.
 */
export function detectFormulaCycle(
  indicators: readonly {
    code: string;
    isDerived: boolean;
    formulas: readonly { isActive: boolean; expression: string }[];
  }[],
): string[] | null {
  const edges = new Map<string, string[]>();
  for (const ind of indicators) {
    if (!ind.isDerived) continue;
    const active = ind.formulas.find((f) => f.isActive);
    if (!active) continue;
    const p = parseFormula(active.expression);
    if (!p.ok) continue;
    edges.set(ind.code, p.refs);
  }
  const state = new Map<string, 1 | 2>(); // 1 = yolda, 2 = bitti
  const path: string[] = [];
  const visit = (code: string): string[] | null => {
    state.set(code, 1);
    path.push(code);
    for (const next of edges.get(code) ?? []) {
      const st = state.get(next);
      if (st === 1) return [...path.slice(path.indexOf(next)), next];
      if (st === undefined && edges.has(next)) {
        const found = visit(next);
        if (found) return found;
      }
    }
    path.pop();
    state.set(code, 2);
    return null;
  };
  for (const code of edges.keys()) {
    if (state.has(code)) continue;
    const found = visit(code);
    if (found) return found;
  }
  return null;
}

/** Aynı kodun birden çok satırda geçtiği id'ler (kural 15 — kod benzersizliği). */
export function duplicateCodeIds(items: readonly { id: string; code: string }[]): Set<string> {
  const byCode = new Map<string, string[]>();
  for (const it of items) {
    const arr = byCode.get(it.code) ?? [];
    arr.push(it.id);
    byCode.set(it.code, arr);
  }
  const out = new Set<string>();
  for (const ids of byCode.values()) if (ids.length > 1) for (const id of ids) out.add(id);
  return out;
}

// ─── Taşıma (↑↓ / sürükle-bırak — kardeşler arasında orderIndex yeniden yazımı) ──

/** Düğümün kardeş listesi (görünüm sırası; yetim üst = kök). Bilinmeyen id → null. */
export function siblingsInTree<T extends TreeRowLike>(
  tree: IndicatorTree<T>,
  id: string,
): { parentKey: string | null; siblings: T[] } | null {
  const row = tree.byId.get(id);
  if (!row) return null;
  const parentKey =
    row.node.parentId && tree.byId.has(row.node.parentId) ? row.node.parentId : null;
  return { parentKey, siblings: tree.childrenOf.get(parentKey) ?? [] };
}

/**
 * Kardeşler arasında bir adım taşır; etkilenen kardeşlerin yeni `orderIndex` değerlerini döner
 * (0..n-1 yeniden numaralanır → yalnız değişenler haritada). Sınırda null.
 */
export function moveAmongSiblings<T extends TreeRowLike>(
  tree: IndicatorTree<T>,
  id: string,
  direction: -1 | 1,
): Map<string, number> | null {
  const sib = siblingsInTree(tree, id);
  return sib ? moveBy(sib.siblings, id, direction) : null;
}

/** Sürükle-bırak: yalnız AYNI üstteki iki satır; farklı üst / no-op → null. */
export function planTreeReorder<T extends TreeRowLike>(
  tree: IndicatorTree<T>,
  draggedId: string,
  overId: string,
  position: DropPosition,
): Map<string, number> | null {
  const a = siblingsInTree(tree, draggedId);
  const b = siblingsInTree(tree, overId);
  if (!a || a.parentKey !== b?.parentKey) return null;
  return planReorder(a.siblings, draggedId, overId, position);
}

// ─── Arama ────────────────────────────────────────────────────────────────────

/** TR-duyarsız içerir; etiket ya da kod. */
export function matchesSearch(item: { label: string; code: string }, needle: string): boolean {
  const q = needle.trim().toLocaleLowerCase("tr");
  if (!q) return true;
  return (
    item.label.toLocaleLowerCase("tr").includes(q) || item.code.toLocaleLowerCase("tr").includes(q)
  );
}

/**
 * Arama için görünür id kümesi: eşleşen düğüm + tüm ataları (bant kapanmasın) + eşleşen
 * kategorinin tüm alt ağacı. Boş arama → null (filtre yok).
 */
export function visibleIdsForSearch<T extends TreeRowLike>(
  tree: IndicatorTree<T>,
  needle: string,
): Set<string> | null {
  if (!needle.trim()) return null;
  const out = new Set<string>();
  for (const r of tree.rows) {
    if (!matchesSearch(r.node, needle)) continue;
    // kendisi + ataları
    let cur: TreeRow<T> | undefined = r;
    while (cur) {
      out.add(cur.node.id);
      cur = cur.node.parentId ? tree.byId.get(cur.node.parentId) : undefined;
    }
    // kategori eşleştiyse alt ağacı
    if (r.hasChildren) {
      const stack = [...(tree.childrenOf.get(r.node.id) ?? [])];
      while (stack.length) {
        const n = stack.pop()!;
        out.add(n.id);
        stack.push(...(tree.childrenOf.get(n.id) ?? []));
      }
    }
  }
  return out;
}

/** Kök kategori başına yaprak sayısı (Katalog sekmeleri: Tümü / Çevresel / …). */
export function leafCountsByRoot<T extends TreeRowLike>(
  tree: IndicatorTree<T>,
): Map<string, number> {
  const out = new Map<string, number>();
  for (const r of tree.rows) {
    if (r.depth === 0) out.set(r.node.id, r.hasChildren ? r.leafCount : 1);
  }
  return out;
}

// ─── CEVIR birim seçici (formül editörü popover'ı) ──────────────────────────────

export interface ConvertSnippetArgs {
  code: string;
  /** hedef birim (görüntü adı) */
  to: string;
  /** kaynak birim; `fromIndicatorUnit` true ise yazılmaz (gösterge biriminden gelir) */
  from?: string | null;
  fromIndicatorUnit: boolean;
}

/**
 * `CEVIR(kod; "GJ")` (kaynak gösterge biriminden geliyorsa kısa yazım) ya da
 * `CEVIR(kod; "kWh"; "GJ")` (kaynak elle seçildiyse uzun yazım).
 */
export function buildConvertSnippet(args: ConvertSnippetArgs): string {
  const to = args.to.trim();
  const from = args.from?.trim() ?? "";
  if (args.fromIndicatorUnit || from === "") return `CEVIR(${args.code}; "${to}")`;
  return `CEVIR(${args.code}; "${from}"; "${to}")`;
}

/**
 * Parçayı imleç konumuna ekler; sol/sağ komşu boşluk ya da parantez değilse tek boşluk koyar.
 * İmleç verilmez ya da aralık dışıysa sona eklenir. Yeni imleç = eklenen parçanın sonu.
 */
export function insertSnippetAtCursor(
  expression: string,
  snippet: string,
  cursor?: number | null,
): { expression: string; cursor: number } {
  const at =
    cursor === undefined || cursor === null || cursor < 0 || cursor > expression.length
      ? expression.length
      : cursor;
  const left = expression.slice(0, at);
  const right = expression.slice(at);
  const needLeft = left !== "" && !/[\s(]$/.test(left);
  const needRight = right !== "" && !/^[\s)]/.test(right);
  const piece = `${needLeft ? " " : ""}${snippet}${needRight ? " " : ""}`;
  const next = `${left}${piece}${right}`;
  return { expression: next, cursor: left.length + piece.length - (needRight ? 1 : 0) };
}

export interface ConvertIssue {
  code: string;
  from: string;
  to: string;
  /** ulaşılabilir hedefler (görüntü adı) — mesajdaki "desteklenenler" listesi */
  reachable: string[];
  message: string;
}

const CEVIR_RE = /CEVIR\(\s*([a-z][a-z0-9_]*)\s*;\s*"([^"]*)"(?:\s*;\s*"([^"]*)")?\s*\)/g;

/**
 * İfadedeki her CEVIR çağrısı için çevrilebilirlik: 3 argümanlıda kaynak dosyadan, 2 argümanlıda
 * `unitsByCode`'dan (göstergenin birimi). Kaynak birim bilinmiyorsa (kod sette yok / birimsiz)
 * sorun üretilmez — sunucu hesaplarken HESAPLANAMADI der. Sözdizimi bozuk olsa da çalışır
 * (regex tarama) → kullanıcı daha yazarken "kWh → ton için çevrim yok (desteklenenler: …)" görür.
 */
export function convertIssues(
  expression: string,
  unitsByCode: ReadonlyMap<string, string | null | undefined> | undefined,
): ConvertIssue[] {
  const out: ConvertIssue[] = [];
  for (const m of expression.matchAll(CEVIR_RE)) {
    const code = m[1]!;
    const explicitFrom = m[3] !== undefined ? m[2] : undefined;
    const to = m[3] ?? m[2] ?? "";
    const from = explicitFrom ?? unitsByCode?.get(code) ?? null;
    if (!from || !to.trim()) continue;
    if (unitConversionFactor(from, to) !== null) continue;
    const reachable = reachableUnits(from);
    const supported = reachable.length > 0 ? reachable.join(", ") : "yok";
    out.push({
      code,
      from,
      to,
      reachable,
      message: `${from} → ${to} için çevrim yok (desteklenenler: ${supported})`,
    });
  }
  return out;
}

/** "1 kWh = 0,0036 GJ" — popover canlı önizlemesi; çevrim yoksa null. */
export function convertPreview(from: string, to: string): string | null {
  const f = unitConversionFactor(from, to);
  if (f === null) return null;
  const n = f.toLocaleString("tr-TR", { maximumSignificantDigits: 6 });
  return `1 ${unitDisplayName(from)} = ${n} ${unitDisplayName(to)}`;
}

/** Ağaç hücresinde gösterilecek birim: şablon (`{kod}/kişi`) set birimleriyle çözülür. */
export function displayUnitFor(
  ind: { unit: string | null },
  unitsByCode: ReadonlyMap<string, string | null> | undefined,
  /** `{}` / `{_alt}` şablonu için hesap grubunun ilk üyesinin birimi (TODO-KPI-126) */
  firstChildUnit?: string | null,
): { text: string; unresolved: boolean; template: string | null } {
  if (ind.unit === null || !isUnitTemplate(ind.unit)) {
    return { text: ind.unit ?? "—", unresolved: false, template: null };
  }
  const r = resolveUnitTemplate(ind.unit, (code) => unitsByCode?.get(code), {
    firstChildUnit: () => firstChildUnit ?? null,
  });
  return { text: r.unit ?? ind.unit, unresolved: r.unresolved, template: ind.unit };
}
